import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import Container from '@grnhse/seedling/lib/birch/components/container';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import {
  Body,
  InlineMarkdown,
  Label,
  Link,
  SecondaryBody,
} from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import {
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import ForwardIcon from '@grnhse/seedling/lib/birch/icons/forward';
import AiMagicIcon from '@grnhse/seedling/lib/birch/icons/ai_magic';
import { PrimaryTag } from '@grnhse/seedling/lib/birch/components/tags';
import { InformationalCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import {
  standardRequest,
  standardRequestWithErrorFlash,
} from 'shared/utils/request';
import type {
  RediscoveryCurrentApplication,
  AiScorecardSummaryInfo,
} from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import noop from 'lodash/noop';
import { scorecardSummaryText } from 'talent_matching/talent_rediscovery/components/table/summary_cell';

const t = withLocalePrefix(
  'talent_matching.panel.rediscovery_application_history'
);

const AI_SUMMARY_TRUNCATE_LENGTH = 155;
const POLLING_INTERVAL = 3000;
const MAX_POLL_ATTEMPTS = 20;

type Props = {
  application: RediscoveryCurrentApplication;
  onSummaryGenerated?: () => void;
};

const AiScorecardSummarySection = ({
  summary,
  onSummaryGenerated,
}: {
  summary: AiScorecardSummaryInfo;
  onSummaryGenerated?: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(!!summary.task_id);
  const abortControllerRef = useRef<AbortController | null>(null);

  const pollForSummary = useCallback(
    async (taskId: number) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      let attempts = 0;

      while (!controller.signal.aborted && attempts < MAX_POLL_ATTEMPTS) {
        attempts++;
        try {
          const { data } = await standardRequest.get(
            summary.check_summary_status_path,
            {
              params: { background_task_id: taskId },
              signal: controller.signal,
            }
          );

          if (data.status === 'error') {
            throw new Error('Failed to generate scorecard summary');
          }

          if (data.status === 'complete') {
            setLoading(false);
            onSummaryGenerated?.();
            return;
          }

          await new Promise((res) => setTimeout(res, POLLING_INTERVAL));
        } catch {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
          return;
        }
      }

      if (!controller.signal.aborted) {
        setLoading(false);
      }
    },
    [summary.check_summary_status_path, onSummaryGenerated]
  );

  const generateSummary = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { id: taskId },
      } = await standardRequestWithErrorFlash.post<{ id: number }>(
        summary.start_summary_path
      );
      await pollForSummary(taskId);
    } catch {
      setLoading(false);
    }
  }, [summary.start_summary_path, pollForSummary]);

  useEffect(() => {
    if (summary.task_id) {
      pollForSummary(summary.task_id);
    }
    return () => abortControllerRef.current?.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sentiment = summary.overall_sentiment;

  const shouldTruncate =
    sentiment && sentiment.length > AI_SUMMARY_TRUNCATE_LENGTH;
  const displaySummary =
    shouldTruncate && !expanded
      ? `${sentiment.slice(0, AI_SUMMARY_TRUNCATE_LENGTH)}...`
      : sentiment;

  return (
    <>
      <Divider />
      <Stack spacing={0.5}>
        <Stack spacing={0} alignItems="flex-start">
          <Stack direction="row" spacing={0.5}>
            <div style={{ flexShrink: 0 }}>
              <Label>{t('ai_scorecard_summary')}</Label>
            </div>
            <AiMagicIcon size="sm" />
          </Stack>
          {!sentiment && !loading && (
            <SecondaryBody metadata>
              {t('ai_scorecard_summary_available')}
            </SecondaryBody>
          )}
          {loading && (
            <SecondaryBody metadata>
              {t('ai_scorecard_summary_generating')}
            </SecondaryBody>
          )}
        </Stack>

        {loading && <SkeletonLoader columnsPerRow={1} rows={3} />}

        {!loading && !sentiment && (
          <div style={{ flexShrink: 0 }}>
            <SecondaryButton onClick={generateSummary} size="md">
              {t('create_summary')}
            </SecondaryButton>
          </div>
        )}

        {!loading && sentiment && (
          <Stack spacing={0.5}>
            {summary.stale && (
              <InformationalCallOut
                headerText={t('ai_scorecard_summary_stale_title')}
                bodyText={
                  <div style={{ maxWidth: '300px' }}>
                    <Body>{t('ai_scorecard_summary_stale_body')}</Body>
                  </div>
                }
                buttonProps={{
                  children: t('ai_scorecard_summary_stale_button'),
                  onClick: generateSummary,
                }}
              />
            )}
            <SecondaryBody>{displaySummary}</SecondaryBody>
            {shouldTruncate && (
              <Stack alignItems="flex-start" sx={{ marginLeft: '-6px' }}>
                <TertiaryButton
                  onClick={() => setExpanded(!expanded)}
                  size="sm"
                >
                  {expanded ? t('view_less') : t('view_more')}
                </TertiaryButton>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
};

const RediscoveryCurrentApplicationCard = ({
  application,
  onSummaryGenerated,
}: Props) => {
  const {
    hiring_plan_name,
    url,
    hiring_plan_url,
    requisition_id,
    application_stage_name,
    milestone,
    rejection_reason,
    positive_rating_percentage,
    ai_scorecard_summary,
  } = application;

  return (
    <Container>
      <Stack spacing={2}>
        <Stack spacing={0}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Link href={hiring_plan_url} target="_blank">
              {hiring_plan_name}
            </Link>
            {application.matching_application && (
              <PrimaryTag text={t('matching_application')} />
            )}
          </Stack>
          {requisition_id && <Body metadata>{requisition_id}</Body>}
        </Stack>

        <Stack spacing={0.5}>
          {application_stage_name && (
            <SecondaryBody>
              <InlineMarkdown
                text={t('reached', { stage: application_stage_name })}
              />
            </SecondaryBody>
          )}
          {milestone && (
            <SecondaryBody>
              <InlineMarkdown text={t('milestone', { milestone })} />
            </SecondaryBody>
          )}
          {rejection_reason && (
            <SecondaryBody>
              <InlineMarkdown
                text={t('rejected_for', { reason: rejection_reason })}
              />
            </SecondaryBody>
          )}
          <SecondaryBody>
            <InlineMarkdown
              text={scorecardSummaryText(positive_rating_percentage, true)}
            />
          </SecondaryBody>
        </Stack>

        {ai_scorecard_summary && (
          <AiScorecardSummarySection
            summary={ai_scorecard_summary}
            onSummaryGenerated={onSummaryGenerated}
          />
        )}

        <Divider />

        <Stack alignItems="flex-start">
          <Link href={url} target="_blank">
            <SecondaryButton
              onClick={() => noop}
              icon={() => (
                <ForwardIcon size="sm" color={FullPalette.GREEN_700} />
              )}
              iconPosition="right"
              size="md"
            >
              {t('go_to_application')}
            </SecondaryButton>
          </Link>
        </Stack>
      </Stack>
    </Container>
  );
};

export default RediscoveryCurrentApplicationCard;
